import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { RiskDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { RiskRegisterRowWithImpactRatings } from '@risksmart-app/drizzle/src/dto/risk.types';
import type {
  GetRisksFlatQuery,
  GetRisksFlatQueryVariables,
  Risk_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetRisksFlatDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback, useMemo, useRef } from 'react';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { RISK_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  normaliseArrayFilter,
  type ResultHistoryWindowArg,
  tagsWhereOnEntity,
  toResultHistoryVariables,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

function mapTrpcRiskToGraphQL(
  risk: RiskRegisterRowWithImpactRatings
): StrictMappedGraphQLSelection<GetRisksFlatQuery['risk'][number]> {
  return {
    // `impactRatings` (current rating per impact) and `impactRatingsForTrend`
    // (last 10 overall) are two distinct projections built by the data layer —
    // they are NOT interchangeable. Aliasing one onto the other is what broke
    // the register's ImpactPerformanceScore (COI-785).
    ...risk,
    controls_aggregate: {
      aggregate: {
        count: risk.controls.length,
      },
    },
    indicators_aggregate: {
      aggregate: {
        count: risk.indicators.length,
      },
    },
    actions_aggregate: {
      aggregate: {
        count: risk.actions.length,
      },
    },
  };
}

type RiskDataWithAppetites = {
  risk: {
    appetites: { appetite?: unknown }[];
  }[];
};
type NormalisedRiskAppetiteSelection<
  TSelection extends { appetite?: unknown },
> = Omit<TSelection, 'appetite'> & {
  appetite: Exclude<TSelection['appetite'], undefined> | null;
};
type NormalisedRisk<TRisk extends RiskDataWithAppetites['risk'][number]> = Omit<
  TRisk,
  'appetites'
> & {
  appetites: NormalisedRiskAppetiteSelection<TRisk['appetites'][number]>[];
};
type NormalisedRiskData<TData extends RiskDataWithAppetites> = Omit<
  TData,
  'risk'
> & {
  risk: NormalisedRisk<TData['risk'][number]>[];
};

function normaliseRiskAppetites<TData extends RiskDataWithAppetites>(
  data: TData | undefined
): NormalisedRiskData<TData> | undefined;
// The overload retains each query path's full row type after normalization.
// eslint-disable-next-line no-redeclare
function normaliseRiskAppetites(data: RiskDataWithAppetites | undefined) {
  if (!data) {
    return undefined;
  }
  const { risk, ...result } = data;

  return {
    ...result,
    risk: risk.map(({ appetites, ...item }) => ({
      ...item,
      appetites: appetites.map(({ appetite, ...selection }) => ({
        ...selection,
        appetite: appetite ?? null,
      })),
    })),
  };
}

/**
 * Maps TRPC risk data to match the GraphQL query structure
 */
export function mapTrpcRisksToGraphQL(
  trpcData: RiskRegisterRowWithImpactRatings[]
): StrictMappedGraphQLSelection<GetRisksFlatQuery> {
  return {
    risk: trpcData.map((risk) => mapTrpcRiskToGraphQL(risk)),
  };
}

type InternalArgs = UseGetRisksFlatArgs & {
  entityIds?: string[];
};

const useRisksFlatGraphqlVariables = (args: InternalArgs) => {
  const { enterpriseRiskId } = args;
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Risk_Bool_Exp>(
    Parent_Type_Enum.Risk
  );

  const base: Risk_Bool_Exp = { ...entityWhere };
  if (enterpriseRiskId) {
    base.enterpriseRiskInstance = {
      ...base.enterpriseRiskInstance,
      EnterpriseRiskId: { _eq: enterpriseRiskId },
    };
  }

  const { where } = buildDashboardWhere({
    base,
    filters: args,
    userId: user?.userId,
    datePredicates: RISK_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.riskFilterConditions,
  });

  const resultHistoryVariables =
    toResultHistoryVariables<GetRisksFlatQueryVariables>(
      args.resultHistoryWindow,
      (bounds) => ({ riskAssessmentResultsWhere: { TestDate: bounds } })
    );

  return { where, ...resultHistoryVariables };
};

const useGetRisksFlatBase = createQueryHook<
  InternalArgs,
  RiskRegisterRowWithImpactRatings[],
  GetRisksFlatQuery
>({
  trpcQueryOptions: (
    trpc,
    {
      enterpriseRiskId,
      entityIds,
      tagTypeIds,
      departmentTypeIds,
      dateRange,
      ownership,
      resultHistoryWindow,
    }
  ) =>
    trpc.frontend.risk.register.queryOptions({
      enterpriseRiskId,
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
      resultHistoryWindow,
    }),
  mapTrpcDataToGraphQL: mapTrpcRisksToGraphQL,
  graphqlDocument: GetRisksFlatDocument,
  graphqlVariables: useRisksFlatGraphqlVariables,
});

export type UseGetRisksFlatArgs = {
  enterpriseRiskId?: string;
} & DashboardFilterArgs<RiskDashboardDateField> &
  ResultHistoryWindowArg;

/**
 * Fetches risks with automatic entity filtering for both GraphQL and tRPC paths.
 * Entity filtering uses useEntityFilter context internally — consumers don't
 * need to pass entityIds. Dashboard filters (COI-691) are all optional.
 */
export const useGetRisksFlat = (params: {
  queryArgs: UseGetRisksFlatArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();
  const result = useGetRisksFlatBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
  const data = useMemo(
    () => normaliseRiskAppetites(result.data),
    [result.data]
  );
  const refetchRef = useRef(result.refetch);
  refetchRef.current = result.refetch;
  const refetch = useCallback(async () => {
    const refetched = await refetchRef.current();

    return { ...refetched, data: normaliseRiskAppetites(refetched.data) };
  }, []);

  return {
    ...result,
    data,
    refetch,
  };
};
